import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Eye, EyeOff, ArrowLeft, ArrowRight, Truck } from "lucide-react";
import { toast } from "sonner";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_bahr-rimal-system/artifacts/xdruqubd_IMG_6467.JPG";

const DriverLoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t, isRTL } = useLanguage();
  
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const ArrowIcon = isRTL ? ArrowRight : ArrowLeft;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const user = await login(formData.username, formData.password);
      
      if (user.role !== "driver") {
        toast.error(isRTL ? "هذه البوابة مخصصة للسائقين فقط" : "This portal is for drivers only");
        setIsLoading(false);
        return;
      }
      
      toast.success(`${t("welcome")}, ${user.full_name || user.username}!`);
      navigate("/driver");
    } catch (error) {
      toast.error(error.response?.data?.detail || t("loginFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0f172a] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <Link
          to="/"
          className="inline-flex items-center text-slate-400 hover:text-white mb-8"
        >
          <ArrowIcon className={`${isRTL ? 'ml-2' : 'mr-2'} h-4 w-4`} />
          {t("backToHome")}
        </Link>
        
        <Card className="border-0 shadow-xl bg-slate-800 border-slate-700">
          <CardHeader className="text-center pb-0">
            <div className="relative mx-auto mb-4">
              <img 
                src={LOGO_URL} 
                alt="بحر الرمال" 
                className="w-20 h-20 rounded-full object-cover border-4 border-[#d4af37]"
              />
              <div className="absolute -bottom-1 -right-1 bg-[#d4af37] rounded-full p-2">
                <Truck className="w-4 h-4 text-[#0f172a]" />
              </div>
            </div>
            <CardTitle className="font-heading text-2xl text-white">
              {isRTL ? "بوابة السائقين" : "Driver Portal"}
            </CardTitle>
            <CardDescription className="text-slate-400">
              {isRTL ? "سجل دخولك للوصول إلى توصيلاتك" : "Sign in to access your deliveries"}
            </CardDescription>
          </CardHeader>
          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="driver-login-form">
              <div>
                <Label htmlFor="username" className="mb-2 block text-slate-300">{t("username")}</Label>
                <Input
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  placeholder={t("username")}
                  required
                  className="h-12 bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
                  data-testid="driver-login-username"
                />
              </div>
              
              <div>
                <Label htmlFor="password" className="mb-2 block text-slate-300">{t("password")}</Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={handleChange}
                    placeholder={t("password")}
                    required
                    className={`h-12 bg-slate-700 border-slate-600 text-white placeholder:text-slate-400 ${isRTL ? 'pl-12' : 'pr-12'}`}
                    data-testid="driver-login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={`absolute ${isRTL ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 text-slate-400 hover:text-white`}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
              
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-[#d4af37] hover:bg-[#d4af37]/90 text-[#0f172a] h-12 font-semibold"
                data-testid="driver-login-submit"
              >
                <Truck className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                {isLoading ? t("loading") : (isRTL ? "دخول للسائقين" : "Driver Login")}
              </Button>
            </form>
            
            <div className="mt-6 text-center">
              <p className="text-slate-400 text-sm">
                {isRTL ? "هل أنت عميل؟" : "Are you a customer?"}{" "}
                <Link to="/login" className="text-[#d4af37] hover:underline font-medium">
                  {isRTL ? "تسجيل دخول العملاء" : "Customer Login"}
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
        
        {/* Install App Prompt */}
        <div className="mt-6 text-center">
          <p className="text-slate-500 text-xs">
            {isRTL 
              ? "💡 يمكنك إضافة هذه الصفحة إلى شاشتك الرئيسية للوصول السريع"
              : "💡 Add this page to your home screen for quick access"}
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default DriverLoginPage;
